import type { ProcessorRuleConfig } from '../types/nodeConfig';
import { NODE_COLORS } from './nodeConfig';
import type { NodeType } from '../types/project';

function snakeKey(key: string): string {
  return key.replace(/([A-Z])/g, '_$1').toLowerCase();
}

function normalizeKeys(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const normalizedKey = key.includes('_') ? key : snakeKey(key);
    if (Array.isArray(value)) {
      out[normalizedKey] = value.map((item) =>
        item && typeof item === 'object' && !Array.isArray(item)
          ? normalizeKeys(item as Record<string, unknown>)
          : item,
      );
    } else if (value && typeof value === 'object') {
      out[normalizedKey] = normalizeKeys(value as Record<string, unknown>);
    } else {
      out[normalizedKey] = value;
    }
  }
  return out;
}

function normalizeRouteWeights(raw: unknown): Array<{ route_id: string; percentage: number }> {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    const x = normalizeKeys(item as Record<string, unknown>);
    const routeId = String(
      x.route_id ?? x.target_route_id ?? x.route ?? x.outgoing_route_id ?? '',
    );
    return {
      route_id: routeId,
      percentage: Number(x.percentage ?? 0),
    };
  });
}

function normalizeRoutingRules(raw: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(raw)) return [];
  return raw.map((rule) => {
    const r = normalizeKeys(rule as Record<string, unknown>);
    const routeId = String(
      r.route_id ?? r.target_route_id ?? r.route ?? r.outgoing_route_id ?? '',
    );
    return {
      ...r,
      route_id: routeId,
      product_type_id: String(r.product_type_id ?? ''),
      percentage: Number(r.percentage ?? 100),
      priority: Number(r.priority ?? 0),
      enabled: r.enabled !== false,
    };
  });
}

function normalizeProcessorRules(raw: unknown): ProcessorRuleConfig[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((rule, index) => {
    const r = normalizeKeys(rule as Record<string, unknown>);
    const inputReqs = (r.input_requirements as unknown[]) ?? [];
    const outputSpecs = (r.output_specs as unknown[]) ?? [];
    return {
      id: String(r.id ?? crypto.randomUUID()),
      name: String(r.name ?? `Rule ${index + 1}`),
      enabled: r.enabled !== false,
      priority: Number(r.priority ?? index),
      logic_mode: (r.logic_mode === 'or' ? 'or' : 'and') as 'and' | 'or',
      input_requirements: inputReqs.map((req) => {
        const x = normalizeKeys(req as Record<string, unknown>);
        return {
          id: String(x.id ?? crypto.randomUUID()),
          incoming_route_id: String(
            x.incoming_route_id ?? x.route_id ?? x.route ?? '',
          ),
          min_count: Number(x.min_count ?? 1),
          product_type_id: String(x.product_type_id ?? '*'),
        };
      }),
      output_specs: outputSpecs.map((spec) => {
        const x = normalizeKeys(spec as Record<string, unknown>);
        const visual = (x.visual_override as Record<string, unknown>) ?? {};
        return {
          id: String(x.id ?? crypto.randomUUID()),
          target_route_id: String(
            x.target_route_id ?? x.route_id ?? x.route ?? '',
          ),
          emit_count: Number(x.emit_count ?? 1),
          processing_delay_seconds: Number(x.processing_delay_seconds ?? 0),
          size_multiplier: Number(x.size_multiplier ?? 1),
          output_product_type_id:
            x.output_product_type_id != null
              ? String(x.output_product_type_id)
              : undefined,
          visual_override: {
            color: visual.color != null ? String(visual.color) : undefined,
            shape: visual.shape != null ? String(visual.shape) : undefined,
            size: visual.size != null ? Number(visual.size) : undefined,
          },
        };
      }),
    };
  });
}

/** Normalize legacy/imported node JSON for the edit dialog. */
export function normalizeLegacyNodeDraft(
  node: Record<string, unknown>,
): Record<string, unknown> {
  let draft = normalizeKeys({ ...node });

  if (!draft.type && draft.node_type) {
    draft.type = draft.node_type;
  }

  const nodeType = String(draft.type ?? draft.node_type ?? 'waypoint');

  if (nodeType === 'processor') {
    draft.processor_rules = normalizeProcessorRules(draft.processor_rules);
    if (draft.capacity == null) draft.capacity = 1;
    if (draft.queue_warning_threshold == null) draft.queue_warning_threshold = 5;
    if (draft.runtime_congestion_warning_enabled == null) {
      draft.runtime_congestion_warning_enabled = true;
    }
  }

  if (nodeType === 'splitter' || nodeType === 'diverger') {
    if (!draft.distribution_type) draft.distribution_type = 'equal';
    draft.routing_rules = normalizeRoutingRules(draft.routing_rules);
    draft.route_weights = normalizeRouteWeights(draft.route_weights);
    if (!Array.isArray(draft.routing_rules)) draft.routing_rules = [];
    if (!Array.isArray(draft.route_weights)) draft.route_weights = [];
  }

  if (nodeType === 'gate') {
    if (!draft.initial_state) draft.initial_state = 'open';
    if (draft.manual_control_enabled == null) draft.manual_control_enabled = true;
    if (!Array.isArray(draft.schedule)) draft.schedule = [];
  }

  if (nodeType === 'merger') {
    if (!draft.mode) draft.mode = 'fifo';
    if (!Array.isArray(draft.route_priorities)) draft.route_priorities = [];
  }

  if (nodeType === 'output') {
    if (!draft.category) draft.category = 'completed';
    if (!Array.isArray(draft.expected_product_type_ids)) {
      draft.expected_product_type_ids = [];
    }
  }

  if (nodeType === 'input' && Array.isArray(draft.product_types)) {
    draft.product_types = (draft.product_types as Array<Record<string, unknown>>).map(
      (productType) => {
        const normalized = normalizeKeys(productType);
        const legacySize = normalized.size_px;
        if (
          (normalized.size == null || Number(normalized.size) <= 0) &&
          legacySize != null &&
          Number(legacySize) > 0
        ) {
          normalized.size = Number(legacySize);
        }
        delete normalized.size_px;
        return normalized;
      },
    );
  }

  if (draft.group != null && typeof draft.group === 'object') {
    const groupObj = draft.group as Record<string, unknown>;
    draft.group =
      (groupObj.name != null ? String(groupObj.name) : '') ||
      (groupObj.id != null ? String(groupObj.id) : '');
  }

  const typeColor = NODE_COLORS[nodeType as NodeType];
  if (typeColor) {
    draft.color = typeColor;
  }

  return draft;
}
